import { useCallback, useEffect, useRef, useState } from 'react';
import { getScrollableParents } from '@app/utils/general';
import { CategoryHSCodeState, PortalPositionState, PortalVerticalDirection } from '../types';

const PORTAL_HEIGHT = 400;

export const usePortal = () => {
  const parentRef = useRef<HTMLButtonElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);

  const setRef = useCallback((ref: HTMLButtonElement | null) => {
    parentRef.current = ref;
  }, []);

  const setPortalRef = useCallback((ref: HTMLDivElement | null) => {
    portalRef.current = ref;
  }, []);

  const [portalPositionState, setPortalPositionState] = useState<PortalPositionState>({
    top: 0,
    bottom: undefined,
    left: 0,
    width: 0,
    direction: PortalVerticalDirection.DOWN,
    isOpen: false,
    componentState: CategoryHSCodeState.DROPDOWN,
  });

  const setComponentStateDropdown = useCallback(() => {
    setPortalPositionState((prevState) => ({
      ...prevState,
      componentState: CategoryHSCodeState.DROPDOWN,
    }));
  }, []);

  const setComponentStateSearch = useCallback(() => {
    setPortalPositionState((prevState) => ({
      ...prevState,
      componentState: CategoryHSCodeState.SEARCH,
    }));
  }, []);

  const getPositionOfPortal = useCallback(() => {
    if (!parentRef.current)
      return {
        top: 0,
        bottom: undefined,
        left: 0,
        width: 0,
        direction: PortalVerticalDirection.DOWN,
      };
    const rect = parentRef.current?.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - (rect?.bottom || 0);
    const direction =
      spaceBelow < PORTAL_HEIGHT ? PortalVerticalDirection.UP : PortalVerticalDirection.DOWN;
    return {
      top: direction === PortalVerticalDirection.DOWN ? rect?.bottom : undefined,
      bottom:
        direction === PortalVerticalDirection.UP ? windowHeight - (rect?.top || 0) : undefined,
      left: (rect?.left || 0) + window.scrollX,
      width: rect?.width,
      direction,
    };
  }, []);

  const closeDropDown = useCallback(() => {
    setPortalPositionState((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  }, []);

  useEffect(() => {
    const setNewPosition = () => {
      const rect = parentRef.current?.getBoundingClientRect() as DOMRect;
      const isInViewport =
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0;

      if (!isInViewport) {
        closeDropDown();
        return;
      }

      setPortalPositionState((prev) => ({
        ...prev,
        ...getPositionOfPortal(),
      }));
    };

    const scrollableParents = getScrollableParents(parentRef.current as HTMLElement);

    scrollableParents.forEach((parent) => {
      parent.addEventListener('scroll', setNewPosition);
    });

    window.addEventListener('resize', setNewPosition);
    window.addEventListener('scroll', setNewPosition);
    return () => {
      window.removeEventListener('resize', setNewPosition);
      window.removeEventListener('scroll', setNewPosition);
      scrollableParents.forEach((parent) => {
        parent.removeEventListener('scroll', setNewPosition);
      });
    };
  }, [getPositionOfPortal, closeDropDown]);

  const { isOpen } = portalPositionState;

  useEffect(() => {
    if (isOpen) {
      const handleClick = (e: MouseEvent) => {
        if (!parentRef.current) return;
        if (!parentRef.current.contains(e.target as Node)) {
          if (portalRef.current && !portalRef.current.contains(e.target as Node)) {
            closeDropDown();
          }
        }
      };
      document.addEventListener('mousedown', handleClick);
      return () => {
        document.removeEventListener('mousedown', handleClick);
      };
    }
    return () => {
      /** */
    };
  }, [isOpen, closeDropDown]);

  const openDropDown = useCallback(() => {
    setPortalPositionState((prev) => ({
      ...prev,
      isOpen: true,
      ...getPositionOfPortal(),
      componentState: CategoryHSCodeState.DROPDOWN,
    }));
  }, [getPositionOfPortal]);

  const toggleOpen = useCallback(() => {
    setPortalPositionState((prev) => {
      const { isOpen } = prev;
      if (!isOpen) {
        return {
          ...prev,
          isOpen: true,
          ...getPositionOfPortal(),
          componentState: CategoryHSCodeState.DROPDOWN,
        };
      }
      return {
        ...prev,
        isOpen: false,
      };
    });
  }, [getPositionOfPortal]);

  return {
    portalPosition: portalPositionState,
    setRef,
    parentRef,
    open: portalPositionState.isOpen,
    toggleOpen,
    setPortalRef,
    setComponentStateDropdown,
    setComponentStateSearch,
    componentState: portalPositionState.componentState,
    closeDropDown,
    portalRef,
    openDropDown,
  };
};
